import { FormEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/providers/AuthProvider";
import { useWebAuthnAuth } from "@/hooks/useWebAuthnAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, Fingerprint } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import { TurnstileCAPTCHA } from "@/components/TurnstileCAPTCHA";
import { AccountLockoutWarning } from "@/components/AccountLockoutWarning";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { z } from "zod";

// Zod validation schemas
const emailSchema = z.string().email("Please enter a valid email address");

const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, "Password must contain at least one special character");

const signUpSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required")
});

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AuthDialog({ open, onOpenChange }: AuthDialogProps) {
  const { signIn, signUp } = useAuth();
  const { isWebAuthnSupported, authenticateWithPasskey, isAuthenticating } = useWebAuthnAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const getUserFriendlyError = (error: any): string => {
    const message = error?.message || String(error);
    if (message.includes('Invalid login credentials')) return "Invalid email or password";
    if (message.includes('Email not confirmed')) return "Please verify your email before signing in";
    if (message.includes('User already registered')) return "An account with this email already exists";
    if (message.includes('Password should be at least')) return "Password must be at least 8 characters";
    if (message.includes('Unable to validate email')) return "Please enter a valid email address";
    if (message.includes('Email rate limit exceeded')) return "Too many attempts. Please try again later";
    return message;
  };

  const handleWebAuthnLogin = async () => {
    if (!email) {
      setError("Please enter your email address first");
      return;
    }

    const result = await authenticateWithPasskey(email);
    if (!result.error) {
      onOpenChange(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    setValidationErrors({});

    // Require CAPTCHA after 3 failed attempts or for signup
    if ((failedAttempts >= 3 || mode === 'signup') && !captchaToken) {
      setError('Please complete the CAPTCHA verification');
      setLoading(false);
      return;
    }

    // Validate input with Zod
    try {
      if (mode === 'login') {
        signInSchema.parse({ email, password });
      } else if (mode === 'signup') {
        signUpSchema.parse({ email, password, confirmPassword });
      } else if (mode === 'forgot') {
        emailSchema.parse(email);
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        err.errors.forEach((error) => {
          const path = error.path.join('.');
          errors[path] = error.message;
        });
        setValidationErrors(errors);
        setError(Object.values(errors)[0]);
        setLoading(false);
        return;
      }
    }

    if (mode === 'forgot') {
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/`,
        });
        if (error) throw error;
        setSuccess("Password reset email sent! Check your inbox.");
        toast({ title: "Email Sent", description: "Check your email for password reset instructions" });
      } catch (error: any) {
        setError(getUserFriendlyError(error));
      }
      setLoading(false);
      return;
    }

    if (mode === 'signup') {
      // Check password breach
      try {
        const { data: breachData, error: breachError } = await supabase.functions.invoke(
          'check-password-breach',
          { body: { password } }
        );

        if (!breachError && breachData?.breached) {
          setError('This password has been exposed in a data breach. Please choose a different password.');
          setLoading(false);
          return;
        }
      } catch (error) {
        // Continue if breach check fails
      }
    }

    if (mode === 'login') {
      try {
        const { error } = await signIn(email, password);
        if (error) {
          setFailedAttempts(prev => prev + 1);
          throw error;
        }
        setFailedAttempts(0);
        setCaptchaToken(null);
        toast({ title: "Welcome back!", description: "You've successfully signed in" });
        onOpenChange(false);
      } catch (error: any) {
        setError(getUserFriendlyError(error));
        setCaptchaToken(null);
      }
    } else if (mode === 'signup') {
      try {
        const { error } = await signUp(email, password);
        if (error) throw error;
        setFailedAttempts(0);
        setCaptchaToken(null);
        setSuccess("Account created! Please check your email to verify your account.");
        toast({ title: "Account Created", description: "Check your email to verify your account" });
      } catch (error: any) {
        setError(getUserFriendlyError(error));
        setCaptchaToken(null);
      }
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'login' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Reset password'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'login' && 'Sign in to access your account'}
            {mode === 'signup' && 'Create a new account to get started'}
            {mode === 'forgot' && 'Enter your email to reset your password'}
          </DialogDescription>
        </DialogHeader>

        <AccountLockoutWarning failedAttempts={failedAttempts} />

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setValidationErrors({}); }}
              placeholder="you@example.com"
              required
            />
            {validationErrors.email && (
              <p className="text-sm text-destructive">{validationErrors.email}</p>
            )}
          </div>

          {mode !== 'forgot' && (
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={e => { setPassword(e.target.value); setValidationErrors({}); }}
                placeholder={mode === 'signup' ? "Min 8 chars with uppercase, numbers, symbols" : ""}
                showPasswordToggle
                required
              />
              {validationErrors.password && (
                <p className="text-sm text-destructive">{validationErrors.password}</p>
              )}
              {mode === 'signup' && (
                <>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Password must contain:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2">
                      <li>At least 8 characters</li>
                      <li>Uppercase and lowercase letters</li>
                      <li>Numbers</li>
                      <li>Special characters (!@#$%^&*...)</li>
                    </ul>
                  </div>
                  <PasswordStrengthMeter
                    password={password}
                    onValidationChange={setIsPasswordValid}
                  />
                </>
              )}
            </div>
          )}

          {mode === 'signup' && (
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setValidationErrors({}); }}
                placeholder="Re-enter password"
                showPasswordToggle
                required
              />
              {validationErrors.confirmPassword && (
                <p className="text-sm text-destructive">{validationErrors.confirmPassword}</p>
              )}
            </div>
          )}

          {(failedAttempts >= 3 || mode === 'signup') && (
            <TurnstileCAPTCHA
              onVerify={(token) => setCaptchaToken(token)}
              onError={(error) => setError(error)}
            />
          )}

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? 'Please wait…' :
              mode === 'login' ? 'Sign in' :
                mode === 'signup' ? 'Create account' :
                  'Send reset email'}
          </Button>

          {mode === 'login' && isWebAuthnSupported() && (
            <Button
              type="button"
              variant="outline"
              onClick={handleWebAuthnLogin}
              disabled={isAuthenticating || !email}
              className="w-full"
            >
              <Fingerprint className="h-4 w-4 mr-2" />
              {isAuthenticating ? 'Authenticating...' : 'Sign in with Biometrics'}
            </Button>
          )}
        </form>

        <div className="space-y-2 text-sm text-center">
          {mode === 'login' && (
            <>
              <p>
                <button
                  type="button"
                  className="underline hover:text-primary"
                  onClick={() => { setMode('forgot'); setError(null); }}
                >
                  Forgot password?
                </button>
              </p>
              <p>
                No account?{' '}
                <button
                  type="button"
                  className="underline hover:text-primary"
                  onClick={() => { setMode('signup'); setError(null); }}
                >
                  Sign up
                </button>
              </p>
            </>
          )}

          {mode === 'signup' && (
            <p>
              Have an account?{' '}
              <button
                type="button"
                className="underline hover:text-primary"
                onClick={() => { setMode('login'); setError(null); }}
              >
                Sign in
              </button>
            </p>
          )}

          {mode === 'forgot' && (
            <p>
              Remember your password?{' '}
              <button
                type="button"
                className="underline hover:text-primary"
                onClick={() => { setMode('login'); setError(null); }}
              >
                Sign in
              </button>
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
